import React, { useState } from 'react';

import { useFormikContext } from 'formik';
import { mutate } from 'swr';

import ChartTitleField from '@components/experiments/plotDisplay/fields/ChartTitleField';
import FullWidthToggleField from '@components/experiments/plotDisplay/fields/FullWidthToggleField';
import TextInput from '@components/forms/TextInput';

import { useExperimentAnnotationContext } from '@contexts/ExperimentAnnotationContext';

import { AnalysisParameters, SeuratMarkerExpressionAnalysisParameters } from '@models/AnalysisParameters';
import Experiment from '@models/Experiment';
import Plot from '@models/Plot';
import { CellScatterPlotDisplayOptionFormValues } from '@models/PlotDisplayOption';
import { getCellCoordinateTypeDisplayName } from '@models/plotDisplayOption/CellScatterPlotDisplayOption';

import Endpoints from '@services/Endpoints';

import CellGroups from '../fields/CellGroups';
import AssaySummaryLegendFieldGroupV3 from '../groups/AssaySummaryLegendFieldGroupV3';
import ManageGroups from '../groups/ManageGroups';
import RangeFieldColorGroup from '../groups/RangeFieldColorGroup';
import ThemeFieldGroup from '../groups/ThemeFieldGroup';

const analysesWithoutCellGroups = ['cell_summary'];

type Props = {
    analysisParameters?: AnalysisParameters | null;
    experiment: Experiment;
    plot: Plot;
    setDisplaySaveDisabled: (value: boolean) => void;
    setUpdateGroupsDisabled: (value: boolean) => void;
    updateGroupsDisabled: boolean;
};
const CellScatterPlotDisplayFields = ({
    analysisParameters,
    experiment,
    plot,
    setDisplaySaveDisabled,
    setUpdateGroupsDisabled,
    updateGroupsDisabled,
}: Props) => {
    const { values, setFieldValue } = useFormikContext<CellScatterPlotDisplayOptionFormValues>();
    const [splitByCluster, setSplitByCluster] = useState(Boolean(values?.annotation_set_id) ?? false);
    const { annotationSets } = useExperimentAnnotationContext();
    const typedParameters = analysisParameters as SeuratMarkerExpressionAnalysisParameters;
    const availableCellCoordinateTypes = typedParameters?.cell_coordinate_types ?? ['umap', 'tsne', 'pc'];
    const isCleanedWorkflow = experiment?.is_cleaned_workflow ?? false;
    const hideCellGroupFiltering = analysesWithoutCellGroups.includes(plot.analysis?.analysis_type ?? '');

    return (
        <>
            <FullWidthToggleField />
            <ChartTitleField placeholder={plot.analysis?.name} />
            <TextInput
                name="custom_options_json.dot_radius"
                label="Point size"
                type="number"
                value={values.custom_options_json?.dot_radius}
                placeholder="2.0"
                onValueChange={(value, _, __) => {
                    setFieldValue('custom_options_json.dot_radius', value);
                }}
                allowNegativeValue={false}
                allowDecimals
                step={0.5}
                min={0}
                useNumericFormat
                decimalScale={1}
            />
            {plot.analysis?.analysis_type !== 'cell_summary' && (
                <section className="mb-8">
                    <p className="field-label">Color scale</p>
                    <RangeFieldColorGroup hintText="expression" />
                </section>
            )}
            <section className="mb-8">
                {typedParameters?.cell_count >= 100_000 ? (
                    <>
                        <TextInput
                            name="downsampling_seed"
                            label="Downsampling seed"
                            type="number"
                            value={values.downsampling_seed ?? 42}
                            onValueChange={(_, __, values) => setFieldValue('downsampling_seed', values?.float ?? 0)}
                            fixedDecimalLength={0}
                            allowNegativeValue={false}
                            step={1}
                            useNumericFormat
                            tooltip={{
                                title: 'Set a seed for downsampling to ensure you consistently get the same subset of data each time you run your analysis.',
                            }}
                        />
                        <TextInput
                            name="downsampling_num"
                            label="Downsampling number"
                            type="number"
                            value={values.downsampling_num ?? 100_000}
                            onValueChange={(_, __, values) => setFieldValue('downsampling_num', values?.float ?? 0)}
                            fixedDecimalLength={0}
                            allowNegativeValue={false}
                            step={1}
                            max={100_000}
                            min={0}
                            useNumericFormat
                            tooltip={{
                                title: 'Set a downsampling number for quicker plot visualization. If the number of cells in your dataset is less than the downsampling number, all cells will be used.',
                            }}
                        />
                    </>
                ) : null}
            </section>
            <section className="mb-8 space-y-4">
                <h4 className="text-dark mb-2 text-lg font-semibold tracking-tight">Cell coordinates</h4>
                <div className="space-y-2">
                    {availableCellCoordinateTypes.map((cellCoordinateType) => (
                        <label className="block cursor-pointer" key={cellCoordinateType}>
                            <input
                                type="radio"
                                className="text-indigo-500"
                                name="cell_coordinate_type"
                                onChange={() => {
                                    setFieldValue('cell_coordinate_type', cellCoordinateType);
                                }}
                                checked={values.cell_coordinate_type === cellCoordinateType}
                            />
                            <span className="ml-2">{getCellCoordinateTypeDisplayName(cellCoordinateType)}</span>
                        </label>
                    ))}
                </div>
            </section>

            {plot.analysis?.analysis_type === 'cell_summary' && <ThemeFieldGroup hideStyle />}

            {hideCellGroupFiltering ? null : (
                <CellGroups
                    experiment={experiment}
                    plot={plot}
                    analysisParameters={typedParameters}
                    annotationSets={annotationSets}
                    isCleanedWorkflow={isCleanedWorkflow}
                >
                    <CellGroups.Header />
                    <CellGroups.Description />
                    <CellGroups.GroupByClusterField
                        checked={splitByCluster}
                        onChange={(e) => {
                            setSplitByCluster((prev) => !prev);
                            if (!e.target.checked) {
                                setFieldValue('annotation_set_id', null);
                            }
                        }}
                    />
                    {splitByCluster && (
                        <CellGroups.AnnotationSetField
                            value={values.annotation_set_id}
                            onChange={(e) => setFieldValue('annotation_set_id', e.target.value)}
                        />
                    )}
                    <CellGroups.VariablesField />
                    <CellGroups.LatentVariablesField />
                    <CellGroups.SubmitButton
                        disabled={updateGroupsDisabled}
                        onClick={() => {
                            mutate(
                                Endpoints.lab.experiment.plot.groupsV3({
                                    experimentId: experiment.uuid,
                                    plotId: plot.uuid,
                                }),
                            );
                        }}
                    />
                </CellGroups>
            )}

            {hideCellGroupFiltering ? (
                <AssaySummaryLegendFieldGroupV3
                    description="Click and drag the legend items to reorder. Plot will only show groups included in the analysis"
                    experiment={experiment}
                    ignoreEmpty
                    isSortable
                    plot={plot}
                    setDisplaySaveDisabled={setDisplaySaveDisabled}
                    setUpdateGroupsDisabled={setUpdateGroupsDisabled}
                    groupOrderName="legend_display_order"
                />
            ) : (
                <ManageGroups
                    plot={plot}
                    experiment={experiment}
                    setDisplaySaveDisabled={setDisplaySaveDisabled}
                    setUpdateGroupsDisabled={setUpdateGroupsDisabled}
                />
            )}
        </>
    );
};

export default CellScatterPlotDisplayFields;
